/**
 * `/crucible/me` identity-probe — the SPA's "am I signed in?" check
 * on boot for the cookie-auth deployment per
 * [Bifrost #87](https://github.com/Volley-Inc/bifrost/pull/87).
 *
 * The colocation design serves the SPA shell from S3 / CloudFront
 * under the same origin as the cap host's ALB. The ALB's Cognito
 * listener rule on `/crucible/*` is configured `OnUnauthenticatedRequest:
 * deny`, so an unauthenticated GET returns 401 directly (not the 302
 * to Cognito that breaks fetch-based detection). The SPA reacts to
 * that 401 by navigating the page to `/login` (a separate ALB rule
 * with `OnUnauthenticatedRequest: authenticate`), which triggers the
 * Cognito hosted-UI redirect and sets the ALB session cookie.
 *
 * Three response shapes the SPA needs to distinguish:
 *
 *   - **signed-in (HTTP 200)** — body `{ userId, authMode }` from
 *     the runtime's `/crucible/me` handler. The SPA proceeds to
 *     construct + connect the cookie-mode `BrowserCrucibleClient`.
 *   - **signed-out (HTTP 401)** — render the signed-out shell so
 *     the user can click through to `/login`. We do NOT auto-
 *     navigate from here; navigation is a user-driven action via
 *     the `beginLogin()` helper.
 *   - **error (anything else, including network failures)** —
 *     surface a generic connect-error state. Includes 5xx from
 *     the runtime, CloudFront 504s, and TypeErrors from `fetch`.
 *
 * Pure-ish function: takes a dependency object so unit tests can
 * inject a fake fetch. Defaults to `globalThis.fetch` for the real
 * SPA call sites.
 */

import type { HttpAuthMode } from './http-auth-types.js';

export type ProbeResult =
  | { kind: 'signed-in'; userId: string; authMode: HttpAuthMode }
  | { kind: 'signed-out' }
  | { kind: 'error'; reason: string };

export interface ProbeDependencies {
  /**
   * fetch implementation. Real callers pass `globalThis.fetch.bind(globalThis)`;
   * tests inject a recorder. The default factory below wires the
   * real fetch.
   */
  fetch: typeof fetch;
  /**
   * The probe endpoint. Defaults to `/crucible/me` per the
   * colocation design. Tests can override to assert the URL.
   */
  probePath: string;
}

/**
 * Default dependencies. Real SPA call sites construct this; tests
 * inject their own.
 */
export function defaultProbeDependencies(): ProbeDependencies {
  return {
    fetch: globalThis.fetch.bind(globalThis),
    probePath: '/crucible/me',
  };
}

/**
 * Issue `GET /crucible/me` and decode the response into a
 * {@link ProbeResult}.
 *
 * The probe is `credentials: 'include'` — same-origin under the
 * colocation deployment so this is the default, but pinning it
 * explicitly makes the cookie-auth dependency obvious to readers
 * and avoids breakage if a future fetch-polyfill changes the
 * default.
 *
 * The function never throws; every failure mode lands in the
 * 'error' result variant. The caller (cookie-boot orchestrator)
 * decides how to surface each shape.
 */
export async function probeIdentity(
  deps: ProbeDependencies = defaultProbeDependencies(),
): Promise<ProbeResult> {
  let res: Response;
  try {
    res = await deps.fetch(deps.probePath, {
      method: 'GET',
      credentials: 'include',
      // Surface the request as same-origin to any future debug
      // tooling — the cookie carries identity, no other headers
      // are needed.
      headers: { accept: 'application/json' },
    });
  } catch (err) {
    // TypeError from fetch (network failure, CORS, DNS, etc.) or
    // any other thrown shape. Collapse to a single error variant.
    return {
      kind: 'error',
      reason: `probe fetch failed: ${(err as Error).message}`,
    };
  }

  if (res.status === 401) {
    return { kind: 'signed-out' };
  }
  if (res.status !== 200) {
    return {
      kind: 'error',
      reason: `probe returned unexpected status ${res.status}`,
    };
  }

  let rawBody: unknown;
  try {
    rawBody = await res.json();
  } catch {
    return { kind: 'error', reason: 'probe response body was not valid JSON' };
  }

  // Bugbot Medium on PR #1235: `res.json()` may resolve to `null` (a
  // valid JSON document), a string, a number, an array, etc. — all
  // valid JSON, none of them an object-with-userId. Without this
  // guard the next `rawBody.userId` access throws a TypeError, which
  // breaks the file's "never throws" contract and leaves the caller
  // (runCookieBoot) stuck in 'probing' phase because it awaits the
  // probe without a try/catch.
  if (rawBody === null || typeof rawBody !== 'object') {
    return {
      kind: 'error',
      reason: 'probe response body was not a JSON object',
    };
  }

  const body = rawBody as { userId?: unknown; authMode?: unknown };

  if (typeof body.userId !== 'string' || body.userId.length === 0) {
    return {
      kind: 'error',
      reason: 'probe response body missing userId',
    };
  }
  if (body.authMode !== 'stub' && body.authMode !== 'alb') {
    return {
      kind: 'error',
      reason: `probe response body has invalid authMode "${String(body.authMode)}"`,
    };
  }

  return {
    kind: 'signed-in',
    userId: body.userId,
    authMode: body.authMode,
  };
}
